import { Routes } from '@angular/router';
import { authGuard, dispatcherGuard, officerGuard, adminGuard, dispatchViewGuard, displayGuard } from './guards/auth.guard';

export const routes: Routes = [
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: 'login', loadComponent: () => import('./components/login/login.component').then(m => m.LoginComponent) },

  // Station display board — deliberately OUTSIDE the shell. A firehouse wall
  // monitor has no navigation, no header and nobody to click them; the page
  // owns the whole screen. Keeping it a sibling of the shell (rather than a
  // child that hides its chrome) is also what lets authGuard bounce display
  // sessions out of every shell route without creating a redirect loop.
  {
    path: 'display',
    loadComponent: () => import('./pages/display-board/display-board.component').then(m => m.DisplayBoardComponent),
    canActivate: [displayGuard],
  },

  // Shell-wrapped routes (flat URLs, shared header + sidebar)
  {
    path: '',
    loadComponent: () => import('./layout/shell/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      { path: 'dispatch-board', loadComponent: () => import('./pages/dispatch-board/dispatch-board.component').then(m => m.DispatchBoardComponent), canActivate: [dispatchViewGuard] },
      { path: 'active-calls',   loadComponent: () => import('./pages/my-calls/my-calls.component').then(m => m.MyCallsComponent) },
      // Legacy URL — kept so existing bookmarks and any in-flight push
      // notifications targeting /my-calls still land on the right page.
      { path: 'my-calls',        redirectTo: 'active-calls', pathMatch: 'full' },
      // History page was removed — its functionality is now split: admin/
      // dispatcher use the Reports tabs (Incidents + Incoming Calls +
      // Members Activity); every role sees their own historical calls
      // under My-Analytics → "My Past Calls".
      { path: 'history',        redirectTo: 'my-analytics', pathMatch: 'full' },
      { path: 'my-analytics',   loadComponent: () => import('./pages/my-analytics/my-analytics.component').then(m => m.MyAnalyticsComponent) },
      { path: 'leaderboard',    loadComponent: () => import('./pages/leaderboard/leaderboard.component').then(m => m.LeaderboardComponent) },
      { path: 'officer-review', loadComponent: () => import('./pages/officer-review/officer-review.component').then(m => m.OfficerReviewComponent), canActivate: [officerGuard] },
      { path: 'staff',          loadComponent: () => import('./pages/staff/staff.component').then(m => m.StaffComponent) },
      { path: 'equipment',      loadComponent: () => import('./pages/equipment/equipment.component').then(m => m.EquipmentComponent) },
      {
        path: 'directory',
        loadComponent: () => import('./pages/directory/directory.component').then(m => m.DirectoryComponent),
        canActivate: [dispatchViewGuard],
        children: [
          { path: 'phonebook', loadComponent: () => import('./pages/phonebook/phonebook.component').then(m => m.PhonebookComponent) },
          { path: 'addresses', loadComponent: () => import('./pages/addresses/addresses.component').then(m => m.AddressesComponent) },
        ],
      },
      { path: 'phonebook',      redirectTo: 'directory/phonebook', pathMatch: 'full' },
      { path: 'addresses',      redirectTo: 'directory/addresses', pathMatch: 'full' },
      { path: 'resources',      loadComponent: () => import('./pages/resources/resources.component').then(m => m.ResourcesComponent) },
      // CSV incident import. Linked from Admin Settings (not the sidebar).
      { path: 'import',         loadComponent: () => import('./pages/import/import.component').then(m => m.ImportComponent), canActivate: [adminGuard] },
      { path: 'reports',        loadComponent: () => import('./components/reports/reports.component').then(m => m.ReportsComponent) },
      { path: 'shift',          loadComponent: () => import('./pages/shift/shift.component').then(m => m.ShiftComponent) },
      // In-app feedback: report a problem / suggest an idea / ask the admins.
      // ':id' is a thread ID, or 'new' (with ?kind=) for the composer. Push
      // notifications for replies deep-link to /feedback/:id.
      { path: 'feedback',       loadComponent: () => import('./pages/feedback/feedback.component').then(m => m.FeedbackComponent) },
      { path: 'feedback/:id',   loadComponent: () => import('./pages/feedback/feedback.component').then(m => m.FeedbackComponent) },
      { path: 'settings',       loadComponent: () => import('./pages/user-settings/user-settings.component').then(m => m.UserSettingsComponent) },
      { path: 'admin-settings', loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent), canActivate: [adminGuard] },
      // Audit feed of admin/dispatcher actions across calls.
      // Only linked from Admin Settings (not the sidebar) since the
      // user asked for it to be tucked away there.
      { path: 'activity-log',   loadComponent: () => import('./pages/activity-log/activity-log.component').then(m => m.ActivityLogComponent), canActivate: [dispatcherGuard] },
      // Live PTT radio audio log — its own sidebar nav item (see NAV_ITEMS in
      // sidebar.component.ts), visible to admin/dispatcher/officer. A raw
      // firehose of every transmission, not something a plain firefighter
      // needs, but distinct enough an audience from admin-only that it
      // doesn't belong tucked inside either Settings page.
      { path: 'ptt-audio',      loadComponent: () => import('./pages/ptt-audio/ptt-audio.component').then(m => m.PttAudioComponent), canActivate: [dispatchViewGuard] },
    ],
  },

  { path: '**', redirectTo: '/login' },
];
